export interface ErrorResponse {
    readonly errors?: Error[]
}

export interface Error {
    readonly code: string
    readonly message?: string
    readonly parameters?: ErrorParameters
}

export interface ErrorParameters {
    readonly [key: string]: string
}

export interface ApiResponse {
    status: number
}

export interface ApiResponseResult<T> {
    data: T | null
    response: ApiResponse
}

export enum HttpMethod {
    DELETE = "DELETE",
    GET = "GET",
    PATCH = "PATCH",
    POST = "POST",
    PUT = "PUT",
}

export interface RequestOptions {
    abortController?: AbortController;
}

interface ApiFetchOptions extends RequestOptions {
    readonly searchParams?: { [key: string]: string };
    readonly params?: any;
    readonly sessionId?: null | undefined | string;
    readonly method?: HttpMethod;
    readonly pathIsUrl?: boolean;
}

export class ApiClient {

    constructor(public readonly baseUrl: string) {
    }

    async get<T>(path: string, opts?: RequestOptions): Promise<T> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<T>(
            path,
            {
                ...opts,
                method: HttpMethod.GET,
                sessionId: sessionId,
            },
        )
    }

    async post<T>(path: string, payload?: any, opts?: RequestOptions): Promise<T> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<T>(
            path,
            {
                ...opts,
                method: HttpMethod.POST,
                sessionId: sessionId,
                params: payload,
            },
        )
    }

    async put<T>(path: string, payload?: any, opts?: RequestOptions): Promise<T> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<T>(
            path,
            {
                ...opts,
                method: HttpMethod.PUT,
                sessionId: sessionId,
                params: payload,
            },
        )
    }

    async delete<T>(path: string, opts?: RequestOptions): Promise<T> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<T>(
            path,
            {
                ...opts,
                method: HttpMethod.DELETE,
                sessionId: sessionId,
            },
        )
    }

    async fetch<T>(path: string, options?: ApiFetchOptions): Promise<T> {
        const result = await this.fetchWithResponse<T>(path, options);
        return result.data as T;
    }

    async fetchWithResponse<T>(path: string, options?: ApiFetchOptions): Promise<ApiResponseResult<T>> {
        const method = options ? (options.method || HttpMethod.POST) : HttpMethod.POST
        let headers = new Headers({
            'Accept': 'application/json',
            "X-Client-Version": __APP_VERSION__,
        })
        if (options && options.sessionId) {
            headers.set('Authorization', `Session ${options.sessionId}`)
        }
        if (method === HttpMethod.PATCH || method === HttpMethod.POST || method === HttpMethod.PUT) {
            headers.set('Content-Type', 'application/json')
        }

        let url
        if (options?.pathIsUrl) {
            url = path
        } else {
            url = `${this.baseUrl}${path}`
        }

        if (options?.searchParams) {
            let first = !url.includes('?')
            for (const [key, value] of Object.entries(options.searchParams)) {
                if (first) {
                    url += `?${key}=${value}`
                    first = false
                } else {
                    url += `&${key}=${value}`
                }
            }
        }

        let response = await fetch(`${url}`, {
            headers: headers,
            method,
            body: options ? JSON.stringify(options.params) : '',
        });

        // (TODO: still needed? => no, because of the improved logout logic)
        // Special check for 'unauthorized' with sessionId
        // if (response.status === 401 && options && options.sessionId) {
        //     window.location.href = window.location.origin
        // }

        if (!response.ok) {
            return { data: null, response: { status: response.status } }
        }

        const body = await response.text();
        let data: T | null = null
        if (body && body.length > 0) {
            data = JSON.parse(body);
        }

        return { data, response: { status: response.status } }
    }

    async parseErrorResponse(response: Response) {

        const json = await response.json()

        let errors: Error[] = Array<Error>()
        Object.keys(json).forEach((property) => {
            if (property === 'errors') {
                let value = json[property]
                if (Array.isArray(value)) {

                    value.forEach((errorJson) => {
                        let code = errorJson["code"]
                        if (code.length === 0) {
                            // Seems to be error
                            return
                        }

                        // TODO: parse parameters

                        errors.push({
                            code: code,
                            message: errorJson["message"],
                        })
                    })
                }
            }
        })

        if (errors.length === 0) {
            errors.push(
                {
                    code: "unknown",
                }
            )
        }

        return {
            errors: errors,
        }
    }
}
